import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Alert, Button, Container, Row } from 'reactstrap';

import AccountModalLink from '../../components/account/AccountModalLink';
import ScrollReset from '../../components/common/ScrollReset';
import SEOTitle from '../../components/head/SEOTitle';
import Page from '../../components/layout/Page';
import LeftSidebarLayout from '../../components/layout/LeftSidebarLayout';
import ContentForState from '../../components/loader/ContentForState';
import BalanceSubUserAccountOverview from '../../components/my-account/BalanceUser/BalanceSubUserAccountOverview';
import BalanceUserAccountOverview from '../../components/my-account/BalanceUser/BalanceUserAccountOverview';
import BalanceUserBalanceAccount from '../../components/my-account/BalanceUser/BalanceUserBalanceAccount';
import MyAccountAddress from '../../components/my-account/MyAccountAddress';
import MyAccountAddressList from '../../components/my-account/MyAccountAddressList';
import MyAccountAddressNew from '../../components/my-account/MyAccountAddressNew';
import MyAccountChangePassword from '../../components/my-account/MyAccountChangePassword';
import MyAccountCreditAccount from '../../components/my-account/MyAccountCreditAccount';
import MyAccountInfo from '../../components/my-account/MyAccountInfo';
import MyAccountNavigation from '../../components/my-account/MyAccountNavigation';
import MyAccountOrder from '../../components/my-account/MyAccountOrder';
import MyAccountOrderList from '../../components/my-account/MyAccountOrderList';
import MyAccountOverview from '../../components/my-account/MyAccountOverview';
import MyAccountPaymentTokenList from '../../components/my-account/MyAccountPaymentTokenList';
import MyAccountPurchasesList from '../../components/my-account/MyAccountPurchasesList';
import MyAccountRecurringOrder from '../../components/my-account/MyAccountRecurringOrder';
import MyAccountRecurringOrderList from '../../components/my-account/MyAccountRecurringOrderList';
import MyAccountUser from '../../components/my-account/MyAccountUser';
import MyAccountUserList from '../../components/my-account/MyAccountUserList';
import MyAccountUserNew from '../../components/my-account/MyAccountUserNew';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AccountStore from '../../store/AccountStore';
import ConfigStore from '../../store/ConfigStore';
import CustomerUserStore from '../../store/CustomerUserStore';
import OrderStore from '../../store/OrderStore';
import PaymentTokenStore from '../../store/PaymentTokenStore';
import AccountModalTab from '../../types/AccountModalTab';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import RequestState from '../../types/RequestState';
import { paramsToQueryIdentifier } from '../../util/query';
import { parse } from '../../util/queryString';
import CustomerProductsPage from '../CustomerProductsPage';
import MyAccountThankYouPage from '../MyAccountThankYouPage';

const createRoute = (path, component) => {
  return { path, component };
};

const ROUTES = [
  createRoute(Paths.MyAccount, MyAccountOverview),
  createRoute(Paths.MyAccountInfo, MyAccountInfo),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountAddressList, MyAccountAddressList),
  createRoute(Paths.MyAccountAddressNew, MyAccountAddressNew),
  createRoute(Paths.MyAccountAddress, MyAccountAddress),
];

const TOKEN_ROUTES = [
  createRoute(Paths.MyAccountPaymentTokenList, MyAccountPaymentTokenList),
];

const NON_BALANCE_ROUTES = [
  createRoute(Paths.MyAccountOrderList, MyAccountOrderList),
];

const BALANCE_ROUTES = [
  createRoute(Paths.MyAccountBalanceUser, BalanceUserAccountOverview),
  createRoute(Paths.MyAccountBalanceUserAccountInfo, MyAccountInfo),
  createRoute(
    Paths.MyAccountBalanceUserPaymentHistory,
    BalanceUserBalanceAccount
  ),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountUserNew, MyAccountUserNew),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage),
];

const BALANCE_USER_ROUTES = [
  createRoute(Paths.MyAccountBalanceSubUser, BalanceSubUserAccountOverview),
];

const RETAILER_ROUTES = [
  createRoute(Paths.CustomerProducts, CustomerProductsPage),
  createRoute(Paths.MyAccountUserList, MyAccountUserList),
  createRoute(Paths.MyAccountUserNew, MyAccountUserNew),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountPurchasesList, MyAccountPurchasesList),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage),
];

const CUSTOMER_USER_ROUTES = [
  createRoute(Paths.MyAccount, MyAccountOverview),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountOrderList, MyAccountOrderList),
  createRoute(Paths.CustomerProducts, CustomerProductsPage),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage),
];

const RETAILER_LIMITED_ROUTES = [
  createRoute(Paths.MyAccountCreditAccount, MyAccountCreditAccount),
];

const RECURRING_ORDER_ROUTES = [
  createRoute(Paths.MyAccountRecurringOrderList, MyAccountRecurringOrderList),
  createRoute(Paths.MyAccountRecurringOrder, MyAccountRecurringOrder),
];

const ORDER_SEARCH_PARAMS = {};

const MyAccountPage = ({
  routeService,
  accountStore,
  configStore,
  orderStore,
  customerUserStore,
  paymentTokenStore,
  location,
  match,
}) => {
  const [creditAccess, setCreditAccess] = useState(false);
  const [tokenAccess, setTokenAccess] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(RequestState.NONE);

  useEffect(() => {
    loadOrders();
    loadCreditAccount();
    loadUsers();
    loadPaymentTokens();
  }, []);

  const loadOrders = () => {
    if (accountStore.loggedIn && !accountStore.isBalanceBuyer) {
      setOrdersLoaded(RequestState.LOADING);
      orderStore
        .loadOrders(ORDER_SEARCH_PARAMS)
        .then(() => setOrdersLoaded(RequestState.LOADED));
    } else {
      setOrdersLoaded(RequestState.LOADED);
    }
  };

  const loadCreditAccount = () => {
    if (accountStore.isRetailer) {
      customerUserStore.loadCreditAccount().then(() => {
        setCreditAccess(true);
      });
    }
  };

  const loadUsers = () => {
    if (accountStore.isRetailer) {
      customerUserStore.loadUsers();
    }
  };

  const loadPaymentTokens = () => {
    if (accountStore.loggedIn) {
      // Check Payment card status before creating route
      paymentTokenStore
        .loadTokens()
        .then(() => {
          setTokenAccess(true);
        })
        .catch(() => {
          // No need for Sentry entry
        });
    }
  };

  const getOrderCount = () => {
    const orderList = orderStore.orderQueryResults.get(
      paramsToQueryIdentifier(ORDER_SEARCH_PARAMS)
    );
    return orderList ? orderList.total : 0;
  };

  const getUserCount = () => {
    return accountStore.isBalanceBuyer
      ? customerUserStore.balanceUsers.size
      : customerUserStore.users.size;
  };

  const getCurrentOrderAuthenticationCode = () => {
    const query = parse(location.search);
    return query.code;
  };

  const hasCreditAccountAccess = () => creditAccess;

  const hasTokenAccess = () => tokenAccess;

  const getAccountRoutes = () => {
    const params = parse(location.search);

    let accountRoutes = ROUTES;

    if (accountStore.isCustomerUser) {
      accountRoutes = CUSTOMER_USER_ROUTES;

      if (accountStore.isUserAllowedAddressModification) {
        accountRoutes = accountRoutes.concat([
          createRoute(Paths.MyAccountAddressList, MyAccountAddressList),
          createRoute(Paths.MyAccountAddressNew, MyAccountAddressNew),
          createRoute(Paths.MyAccountAddress, MyAccountAddress),
        ]);
      }
    } else {
      accountRoutes = accountRoutes.concat(NON_BALANCE_ROUTES);
    }

    if (accountStore.isRetailer) {
      if (!accountStore.isCustomerUser) {
        accountRoutes = accountRoutes.concat(RETAILER_ROUTES);
      }

      if (hasCreditAccountAccess()) {
        accountRoutes = accountRoutes.concat(RETAILER_LIMITED_ROUTES);
      }
    }

    if (configStore.recurringOrder.enabled) {
      accountRoutes = accountRoutes.concat(RECURRING_ORDER_ROUTES);
    }

    if (accountStore.isBalanceBuyer) {
      accountRoutes = BALANCE_ROUTES;
    }

    if (accountStore.isBalanceUser) {
      accountRoutes = BALANCE_USER_ROUTES;
    }

    if (hasTokenAccess() || !!params.tokenizationSuccess) {
      accountRoutes = accountRoutes.concat(TOKEN_ROUTES);
    }

    return accountRoutes;
  };

  const code = getCurrentOrderAuthenticationCode();

  return (
    <Page name={CommonPage.MY_ACCOUNT} className="MyAccountPage">
      <ScrollReset key={match.params.id} />
      <Container className="MyAccountPage__container">
        <ContentForState
          state={ordersLoaded}
          forLoaded={() => (
            <Row className="SidebarLayout">
              <LeftSidebarLayout
                sidebar={
                  <MyAccountNavigation
                    routes={getAccountRoutes()}
                    orderCount={getOrderCount()}
                    userCount={getUserCount()}
                  />
                }
                content={
                  <>
                    <Switch>
                      {accountStore.loggedIn &&
                        getAccountRoutes().map((route) => {
                          return (
                            <Route
                              key={route.path}
                              exact
                              path={routeService.getPath(route.path)}
                              component={route.component}
                            />
                          );
                        })}
                      {(accountStore.loggedIn || code) && (
                        <Route
                          key={Paths.MyAccountOrder}
                          exact
                          path={routeService.getPath(Paths.MyAccountOrder)}
                          render={(props) => (
                            <MyAccountOrder {...props} code={code} />
                          )}
                        />
                      )}
                      {!accountStore.loggedIn && (
                        <Route
                          key="my-account-login"
                          render={() => (
                            <>
                              <SEOTitle
                                title={globalTranslations.myAccountTitle}
                              />
                              <Alert
                                className="MyAccountPage__container-info"
                                color="warning"
                              >
                                <FormattedMessage
                                  id="myAccount.loginInfo"
                                  defaultMessage="By logging in you can see your order history and modify your personal information."
                                />
                              </Alert>
                              <AccountModalLink tab={AccountModalTab.LOGIN}>
                                <Button tag="span" color="primary">
                                  <FormattedMessage
                                    {...globalTranslations.logInSentence}
                                  />
                                </Button>
                              </AccountModalLink>
                            </>
                          )}
                        />
                      )}
                      <Redirect key="my-account-fallback" to="/" />
                    </Switch>
                  </>
                }
              />
            </Row>
          )}
        />
      </Container>
    </Page>
  );
};

MyAccountPage.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  paymentTokenStore: modelOf(PaymentTokenStore).isRequired,
  location: RouterPropTypes.location.isRequired,
  match: RouterPropTypes.match.isRequired,
};

export default withRouter(
  inject(
    'routeService',
    'accountStore',
    'configStore',
    'orderStore',
    'customerUserStore',
    'paymentTokenStore'
  )(observer(MyAccountPage))
);
